import type { Database } from "@punchup/data/supabase.types";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { QueryClient, useQuery } from "@tanstack/react-query";
import { SentryFeatureTags, logException } from "@punchup/sentry";
import {
  VenuePageExternalLink,
  getVenuePageExternalLinks,
} from "../../data/venue_pages";

import { SupabaseClient } from "@supabase/supabase-js";

export function useVenuePageExternalLinks(venuePageId: string): {
  externalLinks: VenuePageExternalLink[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const queryFn = async (): Promise<VenuePageExternalLink[]> => {
    const supabase = createClientComponentClient<Database>();

    try {
      const externalLinks = await getVenuePageExternalLinks(
        supabase,
        venuePageId
      );
      return externalLinks;
    } catch (error) {
      logException(error as Error, {
        extra: {
          data: `error fetching external links for venue page: ${venuePageId}`,
        },
        tags: {
          feature: SentryFeatureTags.VenuePages,
          supabase: true,
        },
      });
      throw error;
    }
  };

  const {
    data: externalLinks = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["venue-page-external-links", venuePageId],
    queryFn,
  });

  return { externalLinks, isLoading, error, refetch };
}

export async function prefetchVenuePageExternalLinks(
  queryClient: QueryClient,
  supabase: SupabaseClient<Database>,
  venuePageId: string
) {
  await queryClient.prefetchQuery({
    queryKey: ["venue-page-external-links", venuePageId],
    queryFn: async () => {
      return await getVenuePageExternalLinks(supabase, venuePageId);
    },
    staleTime: 5 * 60 * 1000,
  });
}
